import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSocket } from "../hooks/useSocket";

interface Room {
  id: string;
  playerCount: number;
  maxPlayers: number;
  createdAt: number;
  hasGame: boolean;
  hostName?: string;
  hostId?: string;
}

export function RoomManagement() {
  const location = useLocation();
  const playerName = (location.state as { playerName?: string })?.playerName || "";
  const navigate = useNavigate();
  const socket = useSocket();

  const [rooms, setRooms] = useState<Room[]>([]);
  const [newRoomId, setNewRoomId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!playerName) {
      // Redirect to lobby if no player name
      navigate("/");
      return;
    }

    // Set up socket listeners
    const handleRoomsList = (data: { rooms: Room[] }) => {
      console.log("[RoomManagement] Rooms list received:", data.rooms);
      setRooms(data.rooms);
      setLoading(false);
    };

    const handleRoomCreated = (data: { roomId: string }) => {
      console.log("[RoomManagement] Room created:", data.roomId);
      setLoading(false);
      // Navigate to waiting room as host
      // Use setTimeout to ensure navigation happens after state updates
      setTimeout(() => {
        navigate(`/room/${data.roomId}`, {
          state: { playerName, isHost: true },
        });
      }, 0);
    };

    const handleRoomJoined = (data: { roomId: string }) => {
      console.log("[RoomManagement] Room joined:", data.roomId);
      setLoading(false);
      // Navigate to waiting room
      // Use setTimeout to ensure navigation happens after state updates
      setTimeout(() => {
        navigate(`/room/${data.roomId}`, {
          state: { playerName, isHost: false },
        });
      }, 0);
    };

    const handleRoomDeleted = (data: { roomId: string }) => {
      console.log("[RoomManagement] Room deleted:", data.roomId);
      // Xóa localStorage nếu room bị xóa là room hiện tại của user
      const savedRoomInfo = localStorage.getItem("uno_room");
      if (savedRoomInfo) {
        try {
          const roomInfo = JSON.parse(savedRoomInfo);
          if (roomInfo.roomId === data.roomId) {
            localStorage.removeItem("uno_room");
          }
        } catch (e) {
          console.error("[RoomManagement] Error parsing saved room info:", e);
        }
      }
      // Refresh rooms list
      socket.listRooms();
    };

    const handleRoomDeletedSuccess = (data: { roomId: string }) => {
      console.log("[RoomManagement] Room deleted successfully:", data.roomId);
      // Xóa localStorage nếu room bị xóa là room hiện tại của user
      const savedRoomInfo = localStorage.getItem("uno_room");
      if (savedRoomInfo) {
        try {
          const roomInfo = JSON.parse(savedRoomInfo);
          if (roomInfo.roomId === data.roomId) {
            localStorage.removeItem("uno_room");
          }
        } catch (e) {
          console.error("[RoomManagement] Error parsing saved room info:", e);
        }
      }
      // Refresh rooms list
      socket.listRooms();
    };

    const handleNewRoomCreated = (data: { 
      room: {
        id: string;
        playerCount: number;
        maxPlayers: number;
        createdAt: number;
        hasGame: boolean;
        hostName?: string;
        hostId?: string;
      };
    }) => {
      console.log("[RoomManagement] New room created:", data.room);
      // Thêm room mới vào danh sách nếu chưa có
      setRooms((prevRooms) => {
        // Kiểm tra xem room đã tồn tại chưa
        const roomExists = prevRooms.some((r) => r.id === data.room.id);
        if (roomExists) {
          // Nếu đã tồn tại, cập nhật thông tin
          return prevRooms.map((r) => 
            r.id === data.room.id ? data.room : r
          );
        }
        // Nếu chưa tồn tại, thêm vào đầu danh sách
        return [data.room, ...prevRooms];
      });
    };

    const handleRoomUpdatedInList = (data: { 
      room: {
        id: string;
        playerCount: number;
        maxPlayers: number;
        createdAt: number;
        hasGame: boolean;
        hostName?: string;
        hostId?: string;
      };
    }) => {
      console.log("[RoomManagement] Room updated in list:", data.room);
      // Cập nhật room trong danh sách
      setRooms((prevRooms) => {
        const roomExists = prevRooms.some((r) => r.id === data.room.id);
        if (roomExists) {
          // Cập nhật thông tin room
          return prevRooms.map((r) => 
            r.id === data.room.id ? data.room : r
          );
        }
        // Nếu room không tồn tại và không phải game đã hoàn thành, thêm vào danh sách
        return [data.room, ...prevRooms];
      });
    };

    const handleError = (data: { message: string }) => {
      console.error("[RoomManagement] Error received:", data.message);
      setError(data.message);
      setLoading(false);
    };

    socket.onRoomsList(handleRoomsList);
    socket.onRoomCreated(handleRoomCreated);
    socket.onRoomJoined(handleRoomJoined);
    socket.onRoomDeleted(handleRoomDeleted);
    socket.onRoomDeletedSuccess(handleRoomDeletedSuccess);
    socket.onNewRoomCreated(handleNewRoomCreated);
    socket.onRoomUpdatedInList(handleRoomUpdatedInList);
    socket.onError(handleError);

    // Request rooms list on mount
    setLoading(true);
    socket.listRooms();

    return () => {
      socket.off("ROOMS_LIST", handleRoomsList);
      socket.off("ROOM_CREATED", handleRoomCreated);
      socket.off("ROOM_JOINED", handleRoomJoined);
      socket.off("ROOM_DELETED", handleRoomDeleted);
      socket.off("ROOM_DELETED_SUCCESS", handleRoomDeletedSuccess);
      socket.off("NEW_ROOM_CREATED", handleNewRoomCreated);
      socket.off("ROOM_UPDATED_IN_LIST", handleRoomUpdatedInList);
      socket.off("ERROR", handleError);
    };
  }, [playerName, navigate, socket]);

  const handleCreateRoom = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!playerName.trim()) {
      setError("Vui lòng nhập tên của bạn");
      setLoading(false);
      return;
    }

    const roomId = newRoomId.trim() || `room-${Date.now()}`;
    // Lưu thông tin vào localStorage để reconnect sau này
    localStorage.setItem("uno_room", JSON.stringify({
      roomId,
      playerName: playerName.trim(),
      isHost: true,
    }));
    socket.createRoom(roomId, playerName.trim());
    setNewRoomId("");
  };

  const handleJoinRoom = (roomId: string) => {
    setError("");
    setLoading(true);

    if (!playerName.trim()) {
      setError("Vui lòng nhập tên của bạn");
      setLoading(false);
      return;
    }

    // Lưu thông tin vào localStorage để reconnect sau này
    localStorage.setItem("uno_room", JSON.stringify({
      roomId,
      playerName: playerName.trim(),
      isHost: false,
    }));
    socket.joinRoom(roomId, playerName.trim());
  };

  const handleDeleteRoom = (roomId: string) => {
    if (window.confirm(`Bạn có chắc chắn muốn xóa room "${roomId}"?`)) {
      setError("");
      setLoading(true);
      socket.deleteRoom(roomId);
    }
  };

  const formatRelativeTime = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (seconds < 60) return "vừa xong";
    if (minutes < 60) return `${minutes} phút trước`;
    if (hours < 24) return `${hours} giờ trước`;
    
    const date = new Date(timestamp);
    return date.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
    });
  };

  // Memoize room cards to prevent unnecessary re-renders
  const roomCards = useMemo(() => {
    return rooms.map((room) => ({
      ...room,
      key: room.id,
    }));
  }, [rooms]);

  // Add global styles for hover effects and responsive design
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      .room-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0,0,0,0.4) !important;
        border-color: rgba(255,255,255,0.3) !important;
      }
      .create-room-button:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(102, 126, 234, 0.5) !important;
      }
      .room-card-button-join:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4) !important;
      }
      .room-card-button-delete:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(229, 62, 62, 0.4) !important;
      }
      .refresh-icon-button:hover:not(:disabled) {
        background: rgba(255,255,255,0.2) !important;
        border-color: rgba(255,255,255,0.3) !important;
        transform: rotate(180deg);
      }
      .loading-spinner {
        animation: spin 1s linear infinite;
      }
      .back-button:hover {
        background: rgba(255,255,255,0.2) !important;
        border-color: rgba(255,255,255,0.3) !important;
      }
      .create-room-input:focus {
        border-color: #667eea !important;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.3) !important;
      }
      @media (max-width: 768px) {
        .room-cards-grid {
          grid-template-columns: 1fr !important;
        }
        .room-card-actions {
          flex-direction: column !important;
        }
        .room-card-button-join,
        .room-card-button-delete {
          width: 100% !important;
        }
        .create-room-form {
          flex-direction: column !important;
        }
      }
      @media (max-width: 480px) {
        .card {
          padding: 20px !important;
        }
        .title {
          font-size: 28px !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header Section */}
        <div style={styles.header}>
          <h1 style={styles.title}>🎮 Game Lobby</h1>
          <p style={styles.subtitle}>Welcome back, {playerName}!</p>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {/* Create Room Card */}
        <div style={styles.createRoomCard}>
          <h2 style={styles.createRoomTitle}>Create New Room</h2>
          <form onSubmit={handleCreateRoom} className="create-room-form" style={styles.createRoomForm}>
            <input
              type="text"
              placeholder="Leave empty to auto-generate"
              value={newRoomId}
              onChange={(e) => setNewRoomId(e.target.value)}
              className="create-room-input"
              style={styles.createRoomInput}
            />
            <button
              type="submit"
              disabled={loading}
              className="create-room-button"
              style={{
                ...styles.createRoomButton,
                ...(loading ? styles.buttonDisabled : {}),
              }}
            >
              {loading ? "Creating..." : "Create Room"}
            </button>
          </form>
          <p style={styles.createRoomHelper}>Share room ID with friends to play together</p>
        </div>

        {/* Room List (Lobby) */}
        <div style={styles.roomListSection}>
          <div style={styles.roomListHeader}>
            <h2 style={styles.roomListTitle}>Available Rooms</h2>
            <button
              onClick={() => {
                setLoading(true);
                socket.listRooms();
              }}
              className="refresh-icon-button"
              style={styles.refreshIconButton}
              disabled={loading}
              title="Refresh room list"
            >
              {loading ? "⟳" : "🔄"}
            </button>
          </div>

          {loading && rooms.length === 0 ? (
            <div style={styles.loadingContainer}>
              <div className="loading-spinner" style={styles.loadingSpinner}>⟳</div>
              <div style={styles.loadingText}>Loading rooms...</div>
            </div>
          ) : rooms.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>🏠</div>
              <div style={styles.emptyStateText}>No rooms available. Create the first one!</div>
            </div>
          ) : (
            <div className="room-cards-grid" style={styles.roomCardsGrid}>
              {roomCards.map((room) => (
                <div key={room.id} className="room-card" style={styles.roomCard}>
                  <div style={styles.roomCardHeader}>
                    <div style={styles.roomCardId}>{room.id}</div>
                    <div style={styles.roomCardStatus}>
                      {room.hasGame ? (
                        <span style={styles.statusBadgeInGame}>🔴 In Game</span>
                      ) : (
                        <span style={styles.statusBadgeWaiting}>🟢 Waiting</span>
                      )}
                    </div>
                  </div>
                  
                  <div style={styles.roomCardBody}>
                    <div style={styles.roomCardPlayerCount}>
                      👥 {room.playerCount} / {room.maxPlayers} players
                    </div>
                    <div style={styles.roomCardTime}>
                      ⏱ {formatRelativeTime(room.createdAt)}
                    </div>
                  </div>

                  <div className="room-card-actions" style={styles.roomCardActions}>
                    {room.playerCount < room.maxPlayers ? (
                      <button
                        onClick={() => handleJoinRoom(room.id)}
                        disabled={loading}
                        className="room-card-button-join"
                        style={{
                          ...styles.roomCardButtonJoin,
                          ...(loading ? styles.buttonDisabled : {}),
                        }}
                      >
                        Join
                      </button>
                    ) : (
                      <div style={styles.roomCardFullBadge}>Full</div>
                    )}
                    {(room.playerCount === 0 || (room.hostName === playerName && room.playerCount > 0)) && (
                      <button
                        onClick={() => handleDeleteRoom(room.id)}
                        disabled={loading}
                        className="room-card-button-delete"
                        style={{
                          ...styles.roomCardButtonDelete,
                          ...(loading ? styles.buttonDisabled : {}),
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={() => navigate("/")}
          className="back-button"
          style={styles.backButton}
        >
          ← Back to Lobby
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    background: "var(--bg)", // Use theme variable for consistency
    padding: "20px",
  },
  card: {
    background: "var(--bg-card)", // Use theme variable for consistency
    // backdropFilter removed for Casual Party (solid backgrounds, no blur)
    borderRadius: "20px",
    padding: "32px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
    maxWidth: "900px",
    width: "100%",
    marginTop: "20px",
    border: "1px solid var(--border-subtle)", // Use theme variable for consistency
  },
  // Header Section
  header: {
    marginBottom: "32px",
    textAlign: "center",
  },
  title: {
    margin: "0 0 8px 0",
    fontSize: "36px",
    fontWeight: "700",
    color: "var(--text)", // Use theme variable for consistency
    letterSpacing: "-0.5px",
  },
  subtitle: {
    margin: "0",
    color: "var(--text-muted)", // Use theme variable for consistency
    fontSize: "18px",
    fontWeight: "500",
  },
  // Create Room Card
  createRoomCard: {
    background: "var(--bg-card-hover)", // Use theme variable for consistency
    borderRadius: "16px",
    padding: "24px",
    marginBottom: "32px",
    border: "1px solid var(--border-subtle)", // Use theme variable for consistency
  },
  createRoomTitle: {
    margin: "0 0 16px 0",
    fontSize: "22px",
    fontWeight: "600",
    color: "var(--text)", // Use theme variable for consistency
  },
  createRoomForm: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginBottom: "8px",
  },
  createRoomInput: {
    padding: "14px 18px",
    fontSize: "16px",
    border: "2px solid var(--border-medium)", // Use theme variable for consistency
    borderRadius: "12px",
    outline: "none",
    transition: "all 0.2s",
    background: "var(--bg-card-hover)", // Use theme variable for consistency
    color: "var(--text)", // Use theme variable for consistency
  },
  createRoomButton: {
    padding: "14px 24px",
    fontSize: "16px",
    fontWeight: "600",
    color: "white",
    background: "var(--btn-primary)", // Use theme variable for consistency
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    transition: "all 0.2s",
    boxShadow: "0 4px 12px color-mix(in srgb, var(--btn-primary) 40%, transparent)",
  },
  createRoomHelper: {
    margin: "8px 0 0 0",
    fontSize: "13px",
    color: "rgba(255,255,255,0.6)",
    fontStyle: "italic",
  },
  // Room List Section
  roomListSection: {
    marginBottom: "24px",
  },
  roomListHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  roomListTitle: {
    margin: "0",
    fontSize: "24px",
    fontWeight: "600",
    color: "#ffffff",
  },
  refreshIconButton: {
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    background: "rgba(255,255,255,0.1)",
    border: "2px solid rgba(255,255,255,0.2)",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "all 0.2s",
    padding: "0",
    color: "#ffffff",
  },
  // Room Cards Grid
  roomCardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "16px",
  },
  roomCard: {
    background: "rgba(40, 40, 60, 0.6)",
    borderRadius: "16px",
    padding: "20px",
    border: "2px solid rgba(255,255,255,0.1)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
    transition: "all 0.3s ease",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  roomCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "4px",
  },
  roomCardId: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#ffffff",
    fontFamily: "monospace",
    letterSpacing: "0.5px",
  },
  roomCardStatus: {
    flexShrink: 0,
  },
  statusBadgeWaiting: {
    display: "inline-block",
    padding: "4px 10px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#ffffff",
    background: "rgba(46, 204, 113, 0.3)",
    borderRadius: "12px",
    border: "1px solid rgba(46, 204, 113, 0.5)",
  },
  statusBadgeInGame: {
    display: "inline-block",
    padding: "4px 10px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#ffffff",
    background: "rgba(231, 76, 60, 0.3)",
    borderRadius: "12px",
    border: "1px solid rgba(231, 76, 60, 0.5)",
  },
  roomCardBody: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    flex: 1,
  },
  roomCardPlayerCount: {
    fontSize: "15px",
    fontWeight: "600",
    color: "rgba(255,255,255,0.9)",
  },
  roomCardTime: {
    fontSize: "13px",
    color: "rgba(255,255,255,0.6)",
  },
  roomCardActions: {
    display: "flex",
    gap: "8px",
    marginTop: "8px",
  },
  roomCardButtonJoin: {
    flex: 1,
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: "600",
    color: "white",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "all 0.2s",
    boxShadow: "0 2px 6px rgba(102, 126, 234, 0.3)",
  },
  roomCardButtonDelete: {
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: "600",
    color: "white",
    background: "#e53e3e",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "all 0.2s",
    boxShadow: "0 2px 6px rgba(229, 62, 62, 0.3)",
  },
  roomCardFullBadge: {
    flex: 1,
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: "600",
    color: "rgba(255,255,255,0.6)",
    background: "rgba(255,255,255,0.1)",
    borderRadius: "10px",
    textAlign: "center",
  },
  // Utility Styles
  buttonDisabled: {
    opacity: "0.6",
    cursor: "not-allowed",
    transform: "none !important",
  },
  error: {
    padding: "14px 18px",
    background: "rgba(231, 76, 60, 0.2)",
    color: "#ffcccc",
    borderRadius: "12px",
    marginBottom: "24px",
    fontSize: "14px",
    fontWeight: "500",
    border: "1px solid rgba(231, 76, 60, 0.5)",
  },
  loadingContainer: {
    padding: "40px 20px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
  },
  loadingSpinner: {
    fontSize: "32px",
    color: "#667eea",
    display: "inline-block",
  },
  loadingText: {
    color: "rgba(255,255,255,0.7)",
    fontSize: "16px",
    fontWeight: "500",
  },
  emptyState: {
    padding: "60px 20px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
  },
  emptyStateIcon: {
    fontSize: "48px",
    marginBottom: "8px",
  },
  emptyStateText: {
    color: "rgba(255,255,255,0.6)",
    fontSize: "16px",
    fontWeight: "500",
  },
  backButton: {
    padding: "12px 24px",
    fontSize: "15px",
    fontWeight: "600",
    color: "#ffffff",
    background: "rgba(255,255,255,0.1)",
    border: "2px solid rgba(255,255,255,0.2)",
    borderRadius: "12px",
    cursor: "pointer",
    transition: "all 0.2s",
    width: "100%",
    marginTop: "24px",
  },
};

